import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

const SUB_TONE_CLASSES = {
	up: 'tw:text-ok',
	down: 'tw:text-danger',
	neutral: 'tw:text-ink-3',
};

// Deterministic per-tile widths for the loading skeleton — mirrors
// DataTable.jsx's SKELETON_WIDTHS so bars don't reshuffle on re-render.
const SKELETON_WIDTHS = ['tw:w-4/5', 'tw:w-3/5', 'tw:w-[72%]', 'tw:w-2/3'];

/**
 * `.stat-strip` — a row of 3-4 derived numbers over a `--border` background,
 * so the 1px gaps between cards double as hairlines. Every value here must be
 * computed at render time from the already-fetched rows — never a stored
 * aggregate, per the spec's "no stored aggregates" rule for this component.
 */
function StatStrip({ stats, loading = false, skeletonCount = 4, bare = false }) {
	const tiles = loading
		? Array.from({ length: skeletonCount }, (_, i) => (
			<div key={i} className='tw:flex tw:flex-col tw:gap-0.5 tw:bg-surface tw:px-[18px] tw:py-[14px]'>
				<span aria-hidden='true' className='el-sk tw:h-[11px] tw:w-2/5' />
				<span
					aria-hidden='true'
					className={classnames('el-sk tw:h-5 tw:mt-1', SKELETON_WIDTHS[i % SKELETON_WIDTHS.length])}
				/>
				<span aria-hidden='true' className='el-sk tw:h-[11px] tw:w-1/3 tw:mt-0.5' />
			</div>
		))
		: stats.map((stat) => (
			<div
				key={stat.label}
				className='tw:flex tw:flex-col tw:gap-0.5 tw:bg-surface tw:px-[18px] tw:py-[14px]'
				style={stat.accent ? { borderLeft: `3px solid var(${stat.accent})` } : undefined}
			>
				<span className='tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.06em] tw:text-ink-3'>
					{stat.label}
				</span>
				<span
					className={classnames(
						'tw:tabular-nums',
						stat.accent ? undefined : 'tw:text-ink-0',
						stat.small ? 'tw:text-[16px] tw:font-semibold' : 'tw:text-[22px] tw:font-semibold',
						stat.mono && 'tw:font-mono',
					)}
					style={stat.accent ? { color: `var(${stat.accent})` } : undefined}
				>
					{stat.value}
				</span>
				{stat.sub && (
					<span className={classnames('tw:text-[11.5px]', SUB_TONE_CLASSES[stat.subTone || 'neutral'])}>
						{stat.sub}
					</span>
				)}
			</div>
		));

	return (
		<div
			className={classnames(
				'tw:grid tw:gap-px tw:overflow-hidden tw:bg-border',
				bare ? 'tw:border-b tw:border-border' : 'tw:rounded-panel',
			)}
			style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}
			aria-busy={loading || undefined}
		>
			{tiles}
		</div>
	);
}

StatStrip.propTypes = {
	stats: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			value: PropTypes.node.isRequired,
			// Drop to the smaller 16px size for dates, so they don't dominate
			// next to money/count values (per the spec).
			small: PropTypes.bool,
			mono: PropTypes.bool,
			sub: PropTypes.node,
			subTone: PropTypes.oneOf(['up', 'down', 'neutral']),
			// CSS var name (e.g. '--color-cat-4') for the left-border/value accent — omit for the neutral default.
			accent: PropTypes.string,
		}),
	).isRequired,
	// When true, renders skeletonCount shimmering placeholder tiles instead of
	// mapping `stats` — for the first fetch, before any real stat exists.
	loading: PropTypes.bool,
	skeletonCount: PropTypes.number,
	// Drops the rounded-panel corners in favor of a flush bottom hairline —
	// for callers embedding the strip inside a panel that already has its
	// own outer border (e.g. ProjectionPanel).
	bare: PropTypes.bool,
};

export default StatStrip;
